import { POPOVER_DEFAULT_WIDTH } from './Cmarkdowntable.constants';
import type {
  CmarkdowntablePopoverEntry,
  CmarkdowntablePopoverPosition,
  CmarkdowntablePopoverRect,
} from './Cmarkdowntable.types';

interface CmarkdowntablePopoverViewInput {
  activeRowIndex: number | null;
  entries: CmarkdowntablePopoverEntry[] | null;
  position: CmarkdowntablePopoverPosition | null;
  manualRect: CmarkdowntablePopoverRect | null;
}

interface ActiveCmarkdowntablePopoverViewInput extends CmarkdowntablePopoverViewInput {
  activeRowIndex: number;
  entries: CmarkdowntablePopoverEntry[];
}

interface CmarkdowntablePopoverPanelFrame {
  left: number;
  top: number;
  width: number;
  height?: number;
}

export interface CmarkdowntablePopoverView {
  activeRowIndex: number;
  entries: CmarkdowntablePopoverEntry[];
  panelFrame: CmarkdowntablePopoverPanelFrame;
}

export function resolveCmarkdowntablePopoverView(
  input: CmarkdowntablePopoverViewInput,
): CmarkdowntablePopoverView | null {
  if (!hasPopoverViewContent(input)) {
    return null;
  }

  return {
    activeRowIndex: input.activeRowIndex,
    entries: input.entries,
    panelFrame: resolvePopoverPanelFrame(input),
  };
}

function hasPopoverViewContent(
  input: CmarkdowntablePopoverViewInput,
): input is ActiveCmarkdowntablePopoverViewInput {
  return input.entries != null
    && input.activeRowIndex != null
    && (input.position != null || input.manualRect != null);
}

function resolvePopoverPanelFrame(
  input: ActiveCmarkdowntablePopoverViewInput,
): CmarkdowntablePopoverPanelFrame {
  return {
    left: resolvePanelLeft(input),
    top: resolvePanelTop(input),
    width: resolvePanelWidth(input),
    height: resolvePanelHeight(input),
  };
}

function resolvePanelLeft(input: ActiveCmarkdowntablePopoverViewInput): number {
  return firstResolvedNumber([input.manualRect?.left, input.position?.left, 0]);
}

function resolvePanelTop(input: ActiveCmarkdowntablePopoverViewInput): number {
  return firstResolvedNumber([input.manualRect?.top, input.position?.top, 0]);
}

function resolvePanelWidth(input: ActiveCmarkdowntablePopoverViewInput): number {
  return firstResolvedNumber([input.manualRect?.width, input.position?.width, POPOVER_DEFAULT_WIDTH]);
}

function resolvePanelHeight(input: ActiveCmarkdowntablePopoverViewInput): number | undefined {
  return input.manualRect?.height;
}

function firstResolvedNumber(values: Array<number | undefined>): number {
  return values.find((value): value is number => value != null) ?? 0;
}
